import React, { useState, useEffect, useCallback } from "react";
import {
  authenticateWithAthletePin,
  markAttendance,
  AthleteEnrollment,
  getTodaysPrograms,
} from "../../api/kioskAPI";
import "./KioskAthletePinEntry.css";

interface KioskAthletePinEntryProps {
  onExit: () => void;
  pinLength?: number;
  /**
   * When false, the no-enrollment fallback (which today triggers the
   * makeup-program selector) instead surfaces a "no class today"
   * error. Default true so tenants like GoY that actively use
   * makeups see no behavioral change.
   */
  allowMakeups?: boolean;
}

type ViewState =
  | "pin-entry"
  | "loading"
  | "select-program"
  | "makeup-select"
  | "success"
  | "error";

interface MakeupProgram {
  id: string;
  label: string;
  slots: Array<{
    id: string;
    slotId: string;
    time: string;
  }>;
}

const KioskAthletePinEntry: React.FC<KioskAthletePinEntryProps> = ({
  onExit,
  pinLength = 4,
  allowMakeups = true,
}) => {
  const [viewState, setViewState] = useState<ViewState>("pin-entry");
  const [pin, setPin] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [athleteName, setAthleteName] = useState("");
  const [athleteId, setAthleteId] = useState("");
  const [enrollments, setEnrollments] = useState<AthleteEnrollment[]>([]);
  const [makeupPrograms, setMakeupPrograms] = useState<MakeupProgram[]>([]);
  const [selectedMakeupProgram, setSelectedMakeupProgram] = useState<MakeupProgram | null>(null);

  const resetState = useCallback(() => {
    setPin("");
    setError(null);
    setAthleteName("");
    setAthleteId("");
    setEnrollments([]);
    setSelectedMakeupProgram(null);
    setViewState("pin-entry");
  }, []);

  const handlePinDigit = useCallback((digit: string) => {
    setError(null);
    setPin((prev) => {
      if (prev.length < 8) {
        return prev + digit;
      }
      return prev;
    });
  }, []);

  const handleBackspace = useCallback(() => {
    setPin((prev) => prev.slice(0, -1));
    setError(null);
  }, []);

  const handleClear = useCallback(() => {
    setPin("");
    setError(null);
  }, []);

  const handleMarkAttendance = async (
    slotId: string,
    isMakeup: boolean = false,
    overrideAthleteId?: string
  ) => {
    setViewState("loading");
    try {
      const idToUse = overrideAthleteId ?? athleteId;
      const result = await markAttendance(idToUse, slotId, isMakeup);
      if (result.success) {
        setViewState("success");
        // Auto-reset after showing success
        setTimeout(() => {
          resetState();
        }, 2000);
      } else {
        setError(result.message || "Failed to mark attendance");
        setViewState("error");
      }
    } catch (err) {
      setError("Failed to mark attendance. Please try again.");
      setViewState("error");
    }
  };

  const handleSubmitPin = async () => {
    if (pin.length < pinLength) {
      setError(`PIN must be at least ${pinLength} digits`);
      return;
    }

    setViewState("loading");
    setError(null);

    try {
      const result = await authenticateWithAthletePin(pin);

      if (result.success && result.data) {
        setAthleteName(result.data.athleteName);
        setAthleteId(result.data.athleteId);

        // Filter enrollments that haven't been marked yet
        const unmarkedEnrollments = result.data.enrollments.filter(
          (e) => !e.alreadyMarked
        );

        setEnrollments(result.data.enrollments);

        if (unmarkedEnrollments.length === 1) {
          // Single enrollment - mark present immediately
          // Pass athleteId explicitly since state hasn't updated yet
          await handleMarkAttendance(unmarkedEnrollments[0].slotId, false, result.data.athleteId);
        } else if (unmarkedEnrollments.length > 1) {
          // Multiple enrollments - let athlete choose
          setViewState("select-program");
        } else if (result.data.enrollments.length > 0 && unmarkedEnrollments.length === 0) {
          // All enrollments already marked
          setError("You're already checked in for all your classes today!");
          setViewState("error");
        } else {
          // No enrollments — offer makeup, OR (when the tenant has
          // disabled makeups) tell the athlete there's no class for
          // them today.
          if (allowMakeups) {
            await loadMakeupPrograms();
          } else {
            setError("You're not enrolled in any classes today.");
            setViewState("error");
          }
        }
      } else {
        setError(result.message || "Invalid PIN");
        setPin("");
        setViewState("pin-entry");
      }
    } catch (err) {
      setError("Authentication failed. Please try again.");
      setPin("");
      setViewState("pin-entry");
    }
  };

  const loadMakeupPrograms = async () => {
    try {
      const programsResponse = await getTodaysPrograms();
      if (programsResponse.success && programsResponse.data.length > 0) {
        const programs: MakeupProgram[] = programsResponse.data.map((p) => ({
          id: p.id,
          label: p.label || p.key,
          slots: p.slots.map((s) => ({
            id: s.id,
            slotId: s.slotId,
            time: s.time,
          })),
        }));
        setMakeupPrograms(programs);
        setViewState("makeup-select");
      } else {
        setError("No classes available today for makeup.");
        setViewState("error");
      }
    } catch (err) {
      setError("Failed to load programs. Please try again.");
      setViewState("error");
    }
  };

  const handleSelectEnrollment = (enrollment: AthleteEnrollment) => {
    if (enrollment.alreadyMarked) {
      setError(`Already checked in for ${enrollment.programLabel}`);
      return;
    }
    handleMarkAttendance(enrollment.slotId);
  };

  const handleSelectMakeupProgram = (program: MakeupProgram) => {
    if (program.slots.length === 1) {
      // Single slot - mark as makeup immediately
      handleMarkAttendance(program.slots[0].id, true);
    } else {
      // Multiple slots - let them choose
      setSelectedMakeupProgram(program);
    }
  };

  const handleSelectMakeupSlot = (slotId: string) => {
    handleMarkAttendance(slotId, true);
  };

  // Keyboard support
  useEffect(() => {
    if (viewState !== "pin-entry") return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key >= "0" && e.key <= "9") {
        handlePinDigit(e.key);
      } else if (e.key === "Backspace") {
        handleBackspace();
      } else if (e.key === "Enter" && pin.length >= pinLength) {
        handleSubmitPin();
      } else if (e.key === "Escape") {
        handleClear();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [viewState, pin, pinLength, handlePinDigit, handleBackspace, handleClear]);

  // Render loading state
  if (viewState === "loading") {
    return (
      <div className="kiosk-athlete-pin">
        <div className="kiosk-athlete-pin__loading">
          <div className="kiosk-athlete-pin__spinner" />
          <p>Processing...</p>
        </div>
      </div>
    );
  }

  // Render success state
  if (viewState === "success") {
    return (
      <div className="kiosk-athlete-pin">
        <div className="kiosk-athlete-pin__success">
          <div className="kiosk-athlete-pin__checkmark">&#10003;</div>
          <h2>Welcome, {athleteName}!</h2>
          <p>You're checked in</p>
        </div>
      </div>
    );
  }

  // Render error state
  if (viewState === "error") {
    return (
      <div className="kiosk-athlete-pin">
        <div className="kiosk-athlete-pin__container">
          <div className="kiosk-athlete-pin__error-display">
            <div className="kiosk-athlete-pin__error-icon">!</div>
            <h2>{athleteName ? `Hi, ${athleteName}` : "Oops!"}</h2>
            <p className="kiosk-athlete-pin__error-message">{error}</p>
            <button
              className="kiosk-athlete-pin__btn kiosk-athlete-pin__btn--primary"
              onClick={resetState}
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Render program selection for enrolled athletes
  if (viewState === "select-program") {
    return (
      <div className="kiosk-athlete-pin">
        <div className="kiosk-athlete-pin__container kiosk-athlete-pin__container--wide">
          <div className="kiosk-athlete-pin__header">
            <h1>Hi, {athleteName}!</h1>
            <p>Select your class to check in</p>
          </div>

          <div className="kiosk-athlete-pin__programs">
            {enrollments.map((enrollment) => (
              <button
                key={enrollment.slotId}
                className={`kiosk-athlete-pin__program-card ${
                  enrollment.alreadyMarked ? "kiosk-athlete-pin__program-card--disabled" : ""
                }`}
                onClick={() => handleSelectEnrollment(enrollment)}
                disabled={enrollment.alreadyMarked}
              >
                <span className="kiosk-athlete-pin__program-name">
                  {enrollment.programLabel}
                </span>
                <span className="kiosk-athlete-pin__program-time">
                  {enrollment.times?.join(", ")}
                </span>
                {enrollment.alreadyMarked && (
                  <span className="kiosk-athlete-pin__program-checked">
                    Already checked in
                  </span>
                )}
              </button>
            ))}
          </div>

          <button
            className="kiosk-athlete-pin__btn kiosk-athlete-pin__btn--secondary"
            onClick={resetState}
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  // Render makeup program selection
  if (viewState === "makeup-select") {
    // If a program is selected, show slot selection
    if (selectedMakeupProgram) {
      return (
        <div className="kiosk-athlete-pin">
          <div className="kiosk-athlete-pin__container kiosk-athlete-pin__container--wide">
            <div className="kiosk-athlete-pin__header">
              <h1>Makeup - {selectedMakeupProgram.label}</h1>
              <p>Select a time slot</p>
            </div>

            <div className="kiosk-athlete-pin__programs">
              {selectedMakeupProgram.slots.map((slot) => (
                <button
                  key={slot.id}
                  className="kiosk-athlete-pin__program-card"
                  onClick={() => handleSelectMakeupSlot(slot.id)}
                >
                  <span className="kiosk-athlete-pin__program-time">
                    {slot.time}
                  </span>
                </button>
              ))}
            </div>

            <button
              className="kiosk-athlete-pin__btn kiosk-athlete-pin__btn--secondary"
              onClick={() => setSelectedMakeupProgram(null)}
            >
              Back
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="kiosk-athlete-pin">
        <div className="kiosk-athlete-pin__container kiosk-athlete-pin__container--wide">
          <div className="kiosk-athlete-pin__header">
            <h1>Hi, {athleteName}!</h1>
            <p>No class scheduled today. Select a makeup class:</p>
          </div>

          <div className="kiosk-athlete-pin__programs">
            {makeupPrograms.map((program) => (
              <button
                key={program.id}
                className="kiosk-athlete-pin__program-card"
                onClick={() => handleSelectMakeupProgram(program)}
              >
                <span className="kiosk-athlete-pin__program-name">
                  {program.label}
                </span>
                <span className="kiosk-athlete-pin__program-slots">
                  {program.slots.length} time slot{program.slots.length !== 1 ? "s" : ""}
                </span>
              </button>
            ))}
          </div>

          <button
            className="kiosk-athlete-pin__btn kiosk-athlete-pin__btn--secondary"
            onClick={resetState}
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  // Render PIN entry (default)
  return (
    <div className="kiosk-athlete-pin">
      <div className="kiosk-athlete-pin__container">
        <div className="kiosk-athlete-pin__header">
          <h1>Enter Your PIN</h1>
          <p>Use your athlete PIN to check in</p>
        </div>

        <div className="kiosk-athlete-pin__pin-display">
          {[...Array(8)].map((_, i) => (
            <div
              key={i}
              className={`kiosk-athlete-pin__pin-dot ${
                i < pin.length ? "kiosk-athlete-pin__pin-dot--filled" : ""
              }`}
            />
          ))}
        </div>

        {error && <div className="kiosk-athlete-pin__error">{error}</div>}

        <div className="kiosk-athlete-pin__keypad">
          {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((digit) => (
            <button
              key={digit}
              className="kiosk-athlete-pin__key"
              onClick={() => handlePinDigit(digit)}
            >
              {digit}
            </button>
          ))}
          <button
            className="kiosk-athlete-pin__key kiosk-athlete-pin__key--action"
            onClick={handleClear}
          >
            Clear
          </button>
          <button
            className="kiosk-athlete-pin__key"
            onClick={() => handlePinDigit("0")}
          >
            0
          </button>
          <button
            className="kiosk-athlete-pin__key kiosk-athlete-pin__key--action"
            onClick={handleBackspace}
          >
            &#9003;
          </button>
        </div>

        <button
          className="kiosk-athlete-pin__submit"
          onClick={handleSubmitPin}
          disabled={pin.length < pinLength}
        >
          Check In
        </button>

        <button
          className="kiosk-athlete-pin__exit"
          onClick={onExit}
        >
          Exit Kiosk Mode
        </button>
      </div>
    </div>
  );
};

export default KioskAthletePinEntry;
